import {
  type PresentationFeedbackItem,
  type PresentationFeedbackResponse,
} from './aliases';
import { type FeedbackItem } from './types';

import { picturePreviewAdapter } from '@/modules/pictures/helpers';
import { type Preview, type ProductPicture } from '@/modules/product/types';

/**
 * Converts new presentation API picture format to legacy preview format
 * Uses the shared picturePreviewAdapter which converts:
 * { formats: { P0: { url, width, height }, P1: {...}, ... } }
 * to { [width]: url, ... } format
 */
function convertPicturePreview(
  preview?: PresentationFeedbackItem['product']['preview']
): Preview {
  if (!preview?.formats) {
    return {};
  }

  // The preview structure from the presentation API matches ProductPicture
  // Both have: { id, formats: { P0: {url, width, height}, ... }, original_width?, original_height?, is_square? }
  return picturePreviewAdapter(preview as ProductPicture);
}

/**
 * Converts a single presentation API feedback item to component format
 * Only converts the picture preview structure - everything else passes through
 */
function convertFeedbackItem(item: PresentationFeedbackItem): FeedbackItem {
  return {
    ...item,
    text: item.text ?? '',
    product: {
      id: item.product.id,
      preview: convertPicturePreview(item.product.preview) as {
        [key: number]: string;
      },
    },
  };
}

export type FeedbackResponse = {
  objects: FeedbackItem[];
  page_info: PresentationFeedbackResponse['page_info'];
};

/**
 * Adapts the presentation API feedback response for component usage
 * Converts picture formats but passes through page_info directly
 */
export function adaptFeedbackResponse(
  response: PresentationFeedbackResponse
): FeedbackResponse {
  return {
    objects: (response.objects || []).map(convertFeedbackItem),
    page_info: response.page_info,
  };
}
